import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { formatCurrency } from "@/lib/utils/money-utils";

interface PriceBadgeProps {
  price: number | null | undefined;
  className?: string;
  showFrom?: boolean;
  isLoading?: boolean;
  showPerHour?: boolean;
}

/**
 * Generic price badge component that displays a price with optional "From" prefix
 * and per-hour suffix. Used for both rooms and studios.
 */
export function PriceBadge({
  price,
  className = "",
  showFrom = true,
  isLoading = false,
  showPerHour = true
}: PriceBadgeProps) {
  // If loading, show skeleton
  if (isLoading) {
    return (
      <Badge className={`bg-white/10 backdrop-blur-sm text-white border-white/20 hover:bg-white/20 ${className}`}>
        <Skeleton className="h-4 w-16" />
      </Badge>
    );
  }

  // If no price, don't render
  if (!price && price !== 0) {
    return null;
  }

  const pricePrefix = showFrom ? "From " : "";

  return (
    <Badge
      className={`bg-on-light/75 backdrop-blur-sm text-on-dark border-text-on-dark border-2 ${className}`}
    >
      <span className="text-xs font-bold text-on-dark-muted" style={{ whiteSpace: "pre" }}>
        {pricePrefix}
      </span>
      <span className="text-sm sm:text-base font-bold text-on-dark">
        {formatCurrency(price)}
      </span>
      {showPerHour && (
        <span className="text-xs font-normal ml-1 text-on-dark-muted">/hr</span>
      )}
    </Badge>
  );
}
